import includes from "lodash/includes";
import isArray from "lodash/isArray";
import some from "lodash/some";

import { AccessLevel, CustomType, CustomTypes, NewValuesSetting } from "../types";
import { getAttribute } from "./get_attribute";

export type PermissionObject = {
  ctAccessLevels: Record<string, AccessLevel>;
  rw?: boolean;
  ro?: boolean;
};

const TYPE_REQUIRES_CURATION = [
  "skills",
  "technical_experience",
  "brands",
  "company",
  "qualifications",
  "clients"
];

const SETTING_REQUIRING_NEW_VALUES_APPROVAL = [
  NewValuesSetting.RequireApproval,
  NewValuesSetting.RequireApprovalLocal
];

const SETTING_ALLOWING_NEW_VALUES = [NewValuesSetting.Allowed, ...SETTING_REQUIRING_NEW_VALUES_APPROVAL];

export const isInSections = (sectionKeys: string[] | string, customType: CustomType) =>
  some(customType.sections, (section) =>
    includes(isArray(sectionKeys) ? sectionKeys : [sectionKeys], section.key)
  );

export const isRankable = (customType: CustomType) => isInSections("rankable", customType);

export const allowsNewValues = (customType: CustomType) => {
  // This block is because API location works different than any other custom type.
  // Allows new does not mean allows new values, it means allows adding
  // new location to the account from global values
  if (customType.value_type === "location") {
    return false;
  }

  const newValues = getAttribute(customType, "newValues");

  return !!newValues && SETTING_ALLOWING_NEW_VALUES.includes(newValues);
};

export const requiresApproval = (customType: CustomType) => {
  const newValues = getAttribute(customType, "newValues");
  return !!newValues && SETTING_REQUIRING_NEW_VALUES_APPROVAL.includes(newValues);
};

export const requiresCuration = (customType: CustomType) => {
  if (!customType.name) {
    return false;
  }
  return TYPE_REQUIRES_CURATION.includes(customType.name);
};

export const getPermittedTypes = (
  customTypes: CustomTypes,
  { ctAccessLevels, rw = true, ro = true }: PermissionObject
) =>
  customTypes.filter((ct) => {
    const ctAccessLevel = ctAccessLevels?.[ct.id];
    return (ro && ctAccessLevel === "ro") || (rw && ctAccessLevel === "rw");
  });

export const loadForSections = (sections: string[] | string, customTypes: CustomTypes) =>
  (customTypes || []).filter((customType) => isInSections(sections, customType));

export const getPrintableCustomType = (customType: CustomType | undefined) =>
  getAttribute(customType, "displayAs") || customType?.name || "";
